export type MascotStage = 1 | 2 | 3 | 4;
export const MASCOT_STAGES: MascotStage[] = [1, 2, 3, 4];
type MascotEyes = "open" | "closed" | "laugh" | "wide";
type MascotMouth = "smile" | "smirk" | "grin" | "open" | "o" | "none";
export type MascotLook = -1 | 0 | 1;

export type MascotSpriteState = {
  stage: MascotStage;
  eyes: MascotEyes;
  mouth: MascotMouth;
  look: MascotLook;
  silhouette: boolean;
  auraFrame: number;
};

type MascotSpriteBounds = { minX: number; minY: number; maxX: number; maxY: number };

export const MASCOT_SPRITE_SIZE = 56;
export const MASCOT_AURA_FRAME_COUNT = 6;

const PALETTE = {
  ink: [20, 20, 20],
  white: [255, 255, 255],
  helmet: [255, 194, 26],
  helmetShade: [217, 143, 0],
  gold: [255, 212, 71],
  goldShade: [201, 140, 0],
  red: [255, 0, 73],
  teal: [62, 230, 216],
  auraOuter: [255, 210, 58],
  auraInner: [255, 243, 166],
  spark: [255, 253, 232],
} as const satisfies Record<string, readonly [number, number, number]>;

type MascotColor = keyof typeof PALETTE;
type PixelBuffer = (MascotColor | null)[];
type Cube = { cx: number; ty: number; a: number; h: number };

const SIZE = MASCOT_SPRITE_SIZE;
const NEIGHBOURS = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
] as const;

// Iso 2:1 bricks: `cx` is the centre column, `ty` the top vertex row, `a` the half width, `h` the side height.
const STAGES: Record<MascotStage, { cube: Cube; hat: "stud" | "helmet" | "crown"; aura: boolean }> = {
  1: { cube: { cx: 28, ty: 25, a: 14, h: 13 }, hat: "stud", aura: false },
  2: { cube: { cx: 28, ty: 25, a: 14, h: 13 }, hat: "helmet", aura: false },
  3: { cube: { cx: 28, ty: 22, a: 16, h: 14 }, hat: "crown", aura: false },
  4: { cube: { cx: 28, ty: 22, a: 16, h: 14 }, hat: "crown", aura: true },
};

const EYES: Record<MascotEyes, string[]> = {
  open: ["ww", "ww", "ww"],
  closed: ["..", "..", "ww"],
  laugh: ["...", ".w.", "w.w"],
  wide: ["www", "wkw", "wkw", "www"],
};
const MOUTHS: Record<MascotMouth, string[]> = {
  smile: ["w..w", ".ww."],
  smirk: ["...w", "www."],
  grin: ["wwww", ".ww."],
  open: ["wwww", "wrrw", ".ww."],
  o: [".w.", "w.w", ".w."],
  none: [],
};
const STAMP_COLORS: Record<string, MascotColor> = { w: "white", k: "ink", r: "red", t: "teal", l: "spark" };

const newBuffer = (): PixelBuffer => new Array<MascotColor | null>(SIZE * SIZE).fill(null);
const pixelAt = (buffer: PixelBuffer, x: number, y: number) =>
  x < 0 || y < 0 || x >= SIZE || y >= SIZE ? null : buffer[y * SIZE + x];
const isEdge = (buffer: PixelBuffer, x: number, y: number) =>
  NEIGHBOURS.some(([dx, dy]) => !pixelAt(buffer, x + dx, y + dy));

const fill = (buffer: PixelBuffer, isInside: (x: number, y: number) => boolean, color: MascotColor) => {
  for (let y = 0; y < SIZE; y++)
    for (let x = 0; x < SIZE; x++) if (isInside(x + 0.5, y + 0.5)) buffer[y * SIZE + x] = color;
};

const paintOver = (target: PixelBuffer, layer: PixelBuffer) => {
  layer.forEach((color, index) => {
    if (color) target[index] = color;
  });
  return target;
};

const withOutline = (buffer: PixelBuffer) =>
  buffer.map((color, index) => {
    if (color) return color;
    const x = index % SIZE;
    const y = Math.floor(index / SIZE);
    return NEIGHBOURS.some(([dx, dy]) => pixelAt(buffer, x + dx, y + dy)) ? "ink" : null;
  });

const stamp = (
  buffer: PixelBuffer,
  rows: string[],
  left: number,
  top: number,
  colors: Record<string, MascotColor> = STAMP_COLORS
) => {
  rows.forEach((row, rowIndex) =>
    [...row].forEach((character, columnIndex) => {
      const color = colors[character];
      const x = left + columnIndex;
      const y = top + rowIndex;
      if (color && x >= 0 && y >= 0 && x < SIZE && y < SIZE) buffer[y * SIZE + x] = color;
    })
  );
};

const ellipse = (x: number, y: number, centerX: number, centerY: number, radiusX: number, radiusY: number) =>
  ((x - centerX) / radiusX) ** 2 + ((y - centerY) / radiusY) ** 2 <= 1;

// The logo: white top, black left face (the mascot face), black right face with two white slots.
const drawCube = ({ cx, ty, a, h }: Cube) => {
  const buffer = newBuffer();
  const middle = ty + a / 2;
  const slotHeight = Math.floor((h - 4) / 2);
  fill(buffer, (x, y) => Math.abs(x - cx) / a + Math.abs(y - middle) / (a / 2) <= 1, "white");
  fill(
    buffer,
    (x, y) => {
      const top = middle + (x - (cx - a)) / 2;
      return x >= cx - a && x <= cx && y >= top && y <= top + h;
    },
    "ink"
  );
  fill(
    buffer,
    (x, y) => {
      const top = ty + a - (x - cx) / 2;
      return x >= cx && x <= cx + a && y >= top && y <= top + h;
    },
    "ink"
  );
  fill(
    buffer,
    (x, y) => {
      const row = y - (ty + a - (x - cx) / 2);
      const inSlot = (row >= 2 && row < 2 + slotHeight) || (row >= 3 + slotHeight && row < 3 + 2 * slotHeight);
      return x >= cx + 2 && x <= cx + a - 1.5 && inSlot;
    },
    "white"
  );
  return withOutline(buffer);
};

const drawStud = (cx: number, cy: number) => {
  const buffer = newBuffer();
  const radius = 6;
  const top = cy - 3;
  fill(
    buffer,
    (x, y) =>
      ellipse(x, y, cx, cy, radius, radius / 2) ||
      (Math.abs(x - cx) <= radius && y >= top && y <= cy) ||
      ellipse(x, y, cx, top, radius, radius / 2),
    "ink"
  );
  fill(buffer, (x, y) => ellipse(x, y, cx, top, radius - 1.5, radius / 2 - 0.8), "white");
  return withOutline(buffer);
};

const drawHelmet = (cx: number, cy: number, a: number) => {
  const buffer = newBuffer();
  const radiusX = a * 0.64;
  const radiusY = a * 0.56;
  const domeCenter = cy - 1;
  const isBrim = (x: number, y: number) => ellipse(x, y, cx, domeCenter, a * 0.95, a * 0.24);
  const isDome = (x: number, y: number) => y <= domeCenter + 0.5 && ellipse(x, y, cx, domeCenter, radiusX, radiusY);
  fill(buffer, isBrim, "helmet");
  fill(buffer, (x, y) => isBrim(x, y) && y > domeCenter + 0.6, "helmetShade");
  fill(buffer, isDome, "helmet");
  fill(buffer, (x, y) => isDome(x, y) && x > cx + radiusX * 0.4, "helmetShade");
  fill(buffer, (x, y) => isDome(x, y) && Math.abs(x - cx) < 1 && y < domeCenter - 0.5, "gold");
  stamp(buffer, ["w", "w"], Math.round(cx - radiusX * 0.55), Math.round(domeCenter - radiusY * 0.65));
  return withOutline(buffer);
};

const drawCrown = (cx: number, cy: number) => {
  const buffer = newBuffer();
  const base = cy + 1;
  const band = cy - 3.5;
  const tip = cy - 12;
  const points = [cx - 7, cx, cx + 7];
  fill(buffer, (x, y) => Math.abs(x - cx) <= 9 && y >= band && y <= base, "gold");
  fill(buffer, (x, y) => Math.abs(x - cx) <= 9 && y > base - 1.2 && y <= base, "goldShade");
  points.forEach((point) =>
    fill(buffer, (x, y) => y >= tip && y < band && Math.abs(x - point) <= (y - tip) * 0.36 + 0.5, "gold")
  );
  points.forEach((point) => stamp(buffer, ["l"], Math.floor(point), Math.round(tip) - 1));
  stamp(buffer, ["rr", "rr"], cx - 1, Math.round(band) + 1);
  stamp(buffer, ["tt", "tt"], cx - 7, Math.round(band) + 1);
  stamp(buffer, ["tt", "tt"], cx + 5, Math.round(band) + 1);
  return withOutline(buffer);
};

const drawFace = (buffer: PixelBuffer, { cx, ty, a, h }: Cube, state: MascotSpriteState, isCrowned: boolean) => {
  const left = cx - a;
  const faceTop = (x: number) => ty + a / 2 + (x + 0.5 - left) / 2;
  const eyeColors = { ...STAMP_COLORS, w: isCrowned ? "teal" : "white" } satisfies Record<string, MascotColor>;
  [left + Math.round(a * 0.22), left + Math.round(a * 0.62)].forEach((eyeColumn, eyeIndex) => {
    const x = eyeColumn + state.look;
    const y = Math.round(faceTop(eyeColumn + 1)) + Math.round(h * 0.3);
    stamp(buffer, EYES[state.eyes], x, y, eyeColors);
    if (isCrowned && state.eyes !== "laugh")
      stamp(buffer, eyeIndex === 0 ? ["ww.", "..w"] : [".ww", "w.."], x - (eyeIndex === 0 ? 1 : 0), y - 3);
  });
  const mouth = MOUTHS[state.mouth];
  const mouthColumn = left + Math.round(a * 0.5);
  const mouthWidth = mouth[0]?.length ?? 0;
  stamp(
    buffer,
    mouth,
    mouthColumn - Math.floor(mouthWidth / 2),
    Math.round(faceTop(mouthColumn)) + Math.round(h * 0.72)
  );
};

const drawBody = (state: MascotSpriteState) => {
  const { cube, hat } = STAGES[state.stage];
  const buffer = drawCube(cube);
  drawFace(buffer, cube, state, hat === "crown");
  const topCenter = cube.ty + cube.a / 2;
  if (hat === "stud") return paintOver(buffer, drawStud(cube.cx, topCenter - 1));
  if (hat === "helmet") return paintOver(buffer, drawHelmet(cube.cx, topCenter, cube.a));
  return paintOver(buffer, drawCrown(cube.cx, topCenter));
};

const toSilhouette = (buffer: PixelBuffer) =>
  buffer.map((color, index): MascotColor | null => {
    if (!color) return null;
    return isEdge(buffer, index % SIZE, Math.floor(index / SIZE)) ? "ink" : "white";
  });

const boundsOf = (buffer: PixelBuffer): MascotSpriteBounds => {
  const bounds = { minX: SIZE, minY: SIZE, maxX: 0, maxY: 0 };
  buffer.forEach((color, index) => {
    if (!color) return;
    const x = index % SIZE;
    const y = Math.floor(index / SIZE);
    bounds.minX = Math.min(bounds.minX, x);
    bounds.maxX = Math.max(bounds.maxX, x);
    bounds.minY = Math.min(bounds.minY, y);
    bounds.maxY = Math.max(bounds.maxY, y);
  });
  return bounds;
};

const hash = (first: number, second: number) => {
  let value = Math.imul(first, 374761393) + Math.imul(second, 668265263);
  value = Math.imul(value ^ (value >>> 13), 1274126177);
  return ((value ^ (value >>> 16)) >>> 0) / 4294967295;
};

// Super warrior aura: distance to the body stretched upwards, threshold flickering per column and frame.
const auraFramesByStage = new Map<MascotStage, PixelBuffer[]>();
const auraFrames = (stage: MascotStage) => {
  const cached = auraFramesByStage.get(stage);
  if (cached) return cached;

  const body = drawBody({ stage, eyes: "open", mouth: "smile", look: 0, silhouette: false, auraFrame: 0 });
  const edges: [number, number][] = [];
  body.forEach((color, index) => {
    const x = index % SIZE;
    const y = Math.floor(index / SIZE);
    if (color && isEdge(body, x, y)) edges.push([x, y]);
  });
  const distances = body.map((color, index) => {
    if (color) return Infinity;
    const x = index % SIZE;
    const y = Math.floor(index / SIZE);
    return Math.min(
      ...edges.map(([edgeX, edgeY]) => Math.hypot(x - edgeX, y < edgeY ? (y - edgeY) * 0.38 : (y - edgeY) * 1.8))
    );
  });
  const bounds = boundsOf(body);

  const frames = Array.from({ length: MASCOT_AURA_FRAME_COUNT }, (_, frame) => {
    const buffer = distances.map((distance, index): MascotColor | null => {
      const x = index % SIZE;
      const y = Math.floor(index / SIZE);
      const noise = hash(x >> 1, frame * 13 + (y >> 3)) * 0.6 + hash((x >> 1) + 1, frame * 13) * 0.4;
      const limit = 2.6 + 4 * noise;
      if (distance > limit) return null;
      if (distance <= 1.6) return "auraInner";
      return distance <= limit - 1.2 ? "auraOuter" : "gold";
    });
    for (let spark = 0; spark < 4; spark++) {
      const x = Math.round(bounds.minX + hash(spark, frame + 50) * (bounds.maxX - bounds.minX));
      const y = Math.round(bounds.minY - 3 - hash(spark + 9, frame + 50) * 7);
      if (y >= 0 && !buffer[y * SIZE + x]) buffer[y * SIZE + x] = "spark";
    }
    return buffer;
  });
  auraFramesByStage.set(stage, frames);
  return frames;
};

const composeSprite = (state: MascotSpriteState) => {
  const body = drawBody(state);
  if (state.silhouette) return toSilhouette(body);
  if (!STAGES[state.stage].aura) return body;
  const aura = auraFrames(state.stage)[state.auraFrame % MASCOT_AURA_FRAME_COUNT] ?? newBuffer();
  return paintOver([...aura], body);
};

// Bounded: stages × faces × looks × aura frames stay in the hundreds.
const pixelsByState = new Map<string, Uint8ClampedArray<ArrayBuffer>>();

export const renderMascotSprite = (state: MascotSpriteState) => {
  const key = JSON.stringify(state);
  const cached = pixelsByState.get(key);
  if (cached) return cached;

  const pixels = new Uint8ClampedArray(SIZE * SIZE * 4);
  composeSprite(state).forEach((color, index) => {
    if (!color) return;
    pixels.set([...PALETTE[color], 255], index * 4);
  });
  pixelsByState.set(key, pixels);
  return pixels;
};

export const isMascotCrowned = (stage: MascotStage) => STAGES[stage].hat === "crown";

export const mascotSpriteBounds = (stage: MascotStage) =>
  boundsOf(drawBody({ stage, eyes: "open", mouth: "smile", look: 0, silhouette: false, auraFrame: 0 }));
